// Assembles the deviceContext payload for /assistant/chat and
// /assistant/briefing — everything only the phone can see: EventKit
// calendars and today's live HealthKit numbers. Also pushes the daily
// health snapshot so the backend has trend history.

import { getEventsForDate } from './deviceCalendar';
import { getDailySummary } from './health';
import { silentFetch } from './api';

function localDate(offsetDays = 0) {
  // Calendar arithmetic, not ms offsets — a DST crossing shifts ms math by an
  // hour and can land on the wrong date.
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// How far ahead device (EventKit) events ride along with each chat/briefing
// request. Server-side tools can only see what the phone shares — beyond this
// window the brain has no view of device calendars and says so.
const UPCOMING_DAYS = [2, 3, 4, 5, 6];

// A summary with every metric at zero means HealthKit gave us nothing (no
// permission, or a data-less device like a fresh install). Pushing it would
// overwrite real snapshots written by the user's other device — so those
// days are treated as "no data", not "zero activity".
const SIGNAL_FIELDS = [
  'steps', 'activeEnergyKcal', 'exerciseMinutes', 'distanceMeters',
  'mindfulnessMinutes', 'sleepMinutes', 'restingHeartRate', 'hrvMs', 'heartRateAvg',
];

function hasHealthSignal(summary) {
  if (!summary) return false;
  if (summary.workouts?.length) return true;
  return SIGNAL_FIELDS.some((f) => Number(summary[f]) > 0);
}

export async function buildDeviceContext() {
  const today = localDate(0);
  const tomorrow = localDate(1);

  const [eventsToday, eventsTomorrow, healthToday, ...upcoming] = await Promise.all([
    getEventsForDate(today),
    getEventsForDate(tomorrow),
    getDailySummary(today),
    ...UPCOMING_DAYS.map((offset) => getEventsForDate(localDate(offset))),
  ]);

  const context = {};
  if (eventsToday?.length) context.calendar = { date: today, events: eventsToday };
  // Tomorrow rides along even when empty — "the phone says nothing tomorrow"
  // is meaningful to the brain, unlike null (fetch failed / no permission).
  if (Array.isArray(eventsTomorrow)) context.calendarTomorrow = { date: tomorrow, events: eventsTomorrow };
  const upcomingDays = UPCOMING_DAYS
    .map((offset, i) => ({ date: localDate(offset), events: upcoming[i] }))
    .filter((d) => Array.isArray(d.events) && d.events.length);
  if (upcomingDays.length) context.calendarUpcoming = upcomingDays;
  if (hasHealthSignal(healthToday)) context.healthToday = healthToday;
  return Object.keys(context).length ? context : null;
}

// Push yesterday's (final) and today's (partial) summaries to the backend.
// Fire-and-forget on app open — silentFetch never toasts.
export async function syncHealthSnapshots() {
  const dates = [localDate(-1), localDate(0)];
  for (const date of dates) {
    const summary = await getDailySummary(date);
    if (hasHealthSignal(summary)) {
      await silentFetch('/me/health/snapshot', 'PUT', summary);
    }
  }
}

// Historical backfill: push any of the last `days` daily summaries the server
// doesn't have yet, so trends aren't empty until a month after install —
// HealthKit holds years of history but the server only accumulates from the
// day access was granted. Idempotent: dates already on the server are skipped
// (never clobbered — another device may have written a fuller row), so
// re-runs are cheap and a day missed under a native timeout is picked up by
// the next run. Sequential on purpose: ~8 HealthKit queries per day, and the
// native bridge must never be flooded with 200+ concurrent calls.
export async function backfillHealthSnapshots(days = 30) {
  const existing = await silentFetch('/me/health?days=31');
  if (!existing) return false; // can't see server state — don't guess
  const have = new Set((existing.items || []).map((i) => i.date));
  // Calendar arithmetic, not ms offsets — a DST crossing inside the window
  // would skip or duplicate a date with ms math.
  const d = new Date();
  for (let i = 0; i < days; i += 1) {
    d.setDate(d.getDate() - 1);
    const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (have.has(date)) continue;
    const summary = await getDailySummary(date);
    // Zero-signal days (pre-ownership, no permission) are "no data", not
    // zeros — never written.
    if (hasHealthSignal(summary)) {
      await silentFetch('/me/health/snapshot', 'PUT', summary);
    }
  }
  return true;
}
